// src/utils/getLanguageHint.ts
//
// Returns the language hint to pass to backend cloud functions in
// every AI-text-generating call. The backend uses this as a
// fallback when the user's Firestore profile doesn't specify a
// primaryLanguage — typically anonymous sessions or users who
// haven't completed Travel Profile onboarding yet.
//
// Resolution order (matches the server-side resolveUserLanguage):
//   1. profile.travelProfile.demographics.primaryLanguage if set
//      (display name like "Spanish") — strongest signal for
//      registered users.
//   2. localStorage `instant-tours-language` set by the
//      LanguageGate. Anonymous users who picked a language at
//      first launch have this populated; it's their explicit
//      choice and overrides browser auto-detection.
//   3. navigator.language (e.g. "es-ES") — browser default. Only
//      applies to users who somehow bypassed the gate (e.g.
//      legacy sessions before the gate shipped, or a deep-linked
//      public share path).
//   4. undefined — server defaults to English.
//
// The localStorage key MUST match the one in
// src/i18n/useLanguagePreference.ts. Kept as a string literal
// here rather than importing from the i18n module to avoid a
// dependency cycle (this util is called by hooks that the i18n
// module itself imports).

import type { UserProfile } from "../stores/authStore";

const LANGUAGE_STORAGE_KEY = 'instant-tours-language';

export function getLanguageHint(
  profile?: UserProfile | null
): string | undefined {
  // 1. Profile language (display name). Take it as-is — the server
  // recognises display names directly.
  const profileLang =
    profile?.travelProfile?.demographics?.primaryLanguage;
  if (typeof profileLang === "string" && profileLang.trim()) {
    return profileLang.trim();
  }

  // 2. LanguageGate selection in localStorage. Same display-name
  // format as the profile field — the gate stores English names
  // ("English", "Spanish") so the wire format stays consistent.
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored && stored.trim()) {
        return stored.trim();
      }
    } catch {
      // localStorage can throw in private mode / quota / disabled —
      // silently fall through to navigator.language.
    }
  }

  // 3. Browser locale — last resort for anyone past the gate
  // without a stored choice.
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }

  return undefined;
}
